import React from "react";
import ReasonDialog from "../common/ReasonDialog";
import { ACTION_CALLS } from "../common/warrantyApi";
import { isAmbiguousFailure } from "../common/clientRequest";
import {
  pendingRequestId,
  recordAnswer,
  unresolvedFor,
  usePendingRequests,
} from "../common/pendingRequests";
import { formatMinor } from "../common/format";
import { ACTION_TEXT } from "../common/opsLabels";

// The one confirmation every resolving action shares: what it does in words (naming the amount when
// money moves), a required reason, a client request id made once per action and reused on a retry.
// `onDone(result)` gets the OpsActionResult; `onRefused(result)` gets a refusal so the screen can
// read its list again when the row moved.
//
// The id and the reason live in `pendingRequests`, keyed by action and target, not in this dialog:
// after a 503 or a network failure (the action may have run) cancelling and reopening the same
// row's action shows the same locked reason and sends the same id, until the service answers
// definitely. `onUnsure` is called when the dialog closes with such an action still unresolved, so
// the screen can read its data again and show whether it went through.
const MONEY_ACTIONS = new Set(["retry_refund", "refund_cover"]);

const OpsActionDialog = ({
  show,
  action,
  targetId,
  amountMinor,
  currency,
  subject,
  accessToken,
  onClose,
  onDone,
  onRefused,
  onUnsure,
}) => {
  usePendingRequests();
  const key = `${action}:${targetId}`;
  const unresolved = unresolvedFor(key);
  const text = ACTION_TEXT[action];

  const confirm = async (reason) => {
    const call = ACTION_CALLS[action];
    if (!call || !targetId) {
      return { ok: false, message: "This action is not available." };
    }
    const result = await call(
      accessToken,
      targetId,
      reason,
      pendingRequestId(key, `${action}:${targetId}:${reason}`)
    );
    const unsure = recordAnswer(key, result, reason);
    if (result.ok) {
      onDone(result.data);
      return { ok: true };
    }
    // Only a definite refusal means the row moved. An ambiguous failure (network, 5xx) keeps the
    // dialog and its request id as they are, so the retry replays instead of acting twice.
    if (onRefused && !isAmbiguousFailure(result)) {
      onRefused(result);
    }
    return { ok: false, message: result.message, unsure };
  };

  const close = () => {
    if (unresolvedFor(key) && onUnsure) {
      onUnsure();
    }
    onClose();
  };

  const hasAmount =
    MONEY_ACTIONS.has(action) && typeof amountMinor === "number" && typeof currency === "string";
  const amountSentence = hasAmount
    ? action === "refund_cover"
      ? ` Amount paid for this cover: ${formatMinor(amountMinor, currency)}.`
      : ` Amount: ${formatMinor(amountMinor, currency)}.`
    : MONEY_ACTIONS.has(action)
    ? " The service did not send an amount for this item; check it on the subscription first."
    : "";

  return (
    <ReasonDialog
      show={show}
      title={text?.title || "Confirm"}
      explanation={`${subject ? `${subject}. ` : ""}${text?.effect || ""}${amountSentence}`}
      confirmLabel={hasAmount ? `${text.label} ${formatMinor(amountMinor, currency)}` : text?.label || "Confirm"}
      initialReason={unresolved ? unresolved.reason || "" : ""}
      reasonLocked={unresolved !== null}
      onConfirm={confirm}
      onClose={close}
    />
  );
};

export default OpsActionDialog;
